import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { FlexLayoutModule } from '@angular/flex-layout';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Subject } from 'rxjs';
import { TaxpayerProfileJson } from 'src/app/main/pages/services/microservices/json/TaxpayerProfile.json';
import { TaxpayerService } from 'src/app/main/pages/services/taxpayer.service';
import { NumberDirective } from 'src/app/shared';
import { FormValidator } from 'src/app/shared/utils/form-validator';

import { LoadingModule } from '../../../generals/loading/loading.module';
import { TaxpayerIdActivityResponse } from '../../../services/microservices/response/taxpayerIdActivity.response';
import { LoadingComponent } from '../../loading/loading.component';

@Component({
  selector: 'app-tax-payer-profile-dialog',
  templateUrl: './taxpayer-profile-dialog.component.html',
  styleUrls: ['./taxpayer-profile-dialog.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    FlexLayoutModule,
    MatInputModule,
    MatButtonModule,
    MatDialogModule,
    MatTooltipModule,
    MatSelectModule,
    MatIconModule,
    LoadingModule,
    NumberDirective,
    LoadingComponent,
  ],
})
export class TaxPayerProfileDialogComponent implements OnInit, OnDestroy {
  readonly dialogRef = inject(MatDialogRef<TaxPayerProfileDialogComponent>);
  public data: { signatureId: number } = inject(MAT_DIALOG_DATA);
  private fb = inject(FormBuilder);
  private fv = inject(FormValidator);
  private service = inject(TaxpayerService);
  private _snackBar = inject(MatSnackBar);
  private _unsubscribeAll!: Subject<any>;
  private isTaxpayerLoaded = false;

  form!: FormGroup;
  isLoading = false;
  notification: any = {};
  txtBtn = 'REGISTRAR';

  ngOnInit(): void {
    this._unsubscribeAll = new Subject<any>();
    this.load();
  }

  private load(): void {
    this.initializeForm();
    this.get(this.data.signatureId);
  }

  get(signatureId: number): void {
    this.isTaxpayerLoaded = false;
    this.isLoading = true;
    this.form.patchValue({ signatureId: signatureId });
    this.service.getBySignatureId(signatureId).subscribe({
      next: response => {
        if (response.data) {
          this.form.patchValue(response.data);
          this.form.patchValue({
            class: response.data.clazz.id,
            type: response.data.type.id,
          });
          this.isTaxpayerLoaded = true;
          this.isLoading = false;
          this.txtBtn = 'ACTUALIZAR';
        }
        this.switchArtisanNumber();
      },
      error: () => {
        this.isLoading = false;
      },
    });
  }

  save(): void {
    this.isLoading = true;
    const dataJson = this.formToJson();
    const service = this.isTaxpayerLoaded
      ? this.service.update(dataJson)
      : this.service.save(dataJson);
    service.subscribe({
      next: response => {
        this.isLoading = false;
        this.dialogRef.close(this.getJsonResult(response.data.id));
      },
      error: () => {
        this.isLoading = false;
        this.openSnack('Ocurrió algo');
      },
    });
  }

  switchSpecialNumber(): void {
    if (this.form.get('class')?.value === 2) {
      this.form.get('specialNumber')?.enable();
    } else {
      this.form.get('specialNumber')?.disable();
      this.form.get('specialNumber')?.setValue('');
    }
  }

  switchArtisanNumber(): void {
    if (this.form.get('artisan')?.value) {
      this.form.get('artisanNumber')?.enable();
    } else {
      this.form.get('artisanNumber')?.disable();
      this.form.get('artisanNumber')?.setValue('');
    }
  }

  private initializeForm(): void {
    this.form = this.fb.group(
      {
        id: [0],
        signatureId: [0],
        type: ['', Validators.required],
        class: ['', Validators.required],
        specialNumber: [{ value: '', disabled: true }, Validators.required],
        artisan: ['', Validators.required],
        artisanNumber: [{ value: '', disabled: true }, Validators.required],
        obligatoryAccounting: ['', Validators.required],
      },
      { updateOn: 'blur' },
    );
    this.fieldNotificationInit();
  }

  private fieldNotificationInit(): void {
    const fields = {
      type: { required: 'Tipo es requerido' },
      class: { required: 'Clase es requerido' },
      specialNumber: { required: 'Número Especial es requerido' },
      artisan: { required: 'Artesano es requerido' },
      artisanNumber: { required: 'Número Artesano es requerido' },
      obligatoryAccounting: { required: 'Obligado Contabilidad es requerido' },
    };
    this.fv.setFormValidator(this.form, this.notification, fields);
  }

  controlHasError(formControlName: string): boolean {
    return this.fv.fieldHasError(formControlName);
  }

  private formToJson(): TaxpayerProfileJson {
    const json: TaxpayerProfileJson = {
      id: this.form.controls['id'].value,
      signatureId: this.form.controls['signatureId'].value,
      artisan: this.form.controls['artisan'].value,
      artisanNumber: this.form.controls['artisanNumber'].value,
      obligatoryAccounting: this.form.controls['obligatoryAccounting'].value,
      type: {
        id: this.form.controls['type'].value,
        description: '',
      },
      clazz: {
        id: this.form.controls['class'].value,
        description: '',
      },
      specialNumber: this.form.controls['specialNumber'].value,
    };
    return json;
  }

  private getJsonResult(id: number): TaxpayerIdActivityResponse {
    const json: TaxpayerIdActivityResponse = {
      id: id,
      activity: [],
    };
    return json;
  }

  private openSnack(message: string): void {
    this._snackBar.open(message, '', {
      verticalPosition: 'top',
      horizontalPosition: 'right',
      panelClass: ['snack-error'],
      duration: 5000,
    });
  }

  ngOnDestroy(): void {
    this._unsubscribeAll.next(void 0);
    this._unsubscribeAll.complete();
  }
}
